import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { SERVICES } from "@/lib/site";

const initialState = { name: "", email: "", phone: "", service: "", message: "" };

export default function ContactForm({ compact = false, defaultService = "" }) {
  const [form, setForm] = useState({ ...initialState, service: defaultService });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — your enquiry has been received.", {
        description: "We will be in touch shortly.",
      });
      setForm({ ...initialState });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form" noValidate>
      {!compact && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Enquiry Form</div>
          <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0A1A36]">
            Send us a message
          </h3>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="name" label="Full Name *">
          <Input
            id="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name"
            required
            className="rounded-none h-12 border-slate-300 focus-visible:ring-1 focus-visible:ring-[#0A1A36] focus-visible:border-[#0A1A36]"
            data-testid="contact-form-name"
          />
        </Field>
        <Field id="email" label="Email *">
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            required
            className="rounded-none h-12 border-slate-300 focus-visible:ring-1 focus-visible:ring-[#0A1A36] focus-visible:border-[#0A1A36]"
            data-testid="contact-form-email"
          />
        </Field>
        <Field id="phone" label="Phone">
          <Input
            id="phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+679 ..."
            className="rounded-none h-12 border-slate-300 focus-visible:ring-1 focus-visible:ring-[#0A1A36] focus-visible:border-[#0A1A36]"
            data-testid="contact-form-phone"
          />
        </Field>
        <Field id="service" label="Service of interest">
          <Select value={form.service} onValueChange={(v) => update("service", v)}>
            <SelectTrigger
              id="service"
              className="rounded-none h-12 border-slate-300 focus:ring-1 focus:ring-[#0A1A36] focus:border-[#0A1A36]"
              data-testid="contact-form-service"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {SERVICES.map((s) => (
                <SelectItem
                  key={s.slug}
                  value={s.title}
                  className="rounded-none"
                  data-testid={`contact-form-service-option-${s.slug}`}
                >
                  {s.title}
                </SelectItem>
              ))}
              <SelectItem value="General Enquiry" className="rounded-none" data-testid="contact-form-service-option-general">
                General Enquiry
              </SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field id="message" label="Message *">
        <Textarea
          id="message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          rows={6}
          placeholder="Tell us about your project — location, scope, timeline..."
          required
          className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-[#0A1A36] focus-visible:border-[#0A1A36]"
          data-testid="contact-form-message"
        />
      </Field>

      <div className="pt-2">
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-none h-12 px-7 bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover disabled:opacity-60"
          data-testid="contact-form-submit"
        >
          {submitting ? "Sending…" : (
            <>
              Send Enquiry <Send className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
        <p className="mt-3 text-xs text-slate-500">We typically respond within one business day.</p>
      </div>
    </form>
  );
}

const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] font-bold text-[#0A1A36]">
      {label}
    </Label>
    {children}
  </div>
);
